import { Printer } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAssignees } from "../../../api/accountQueries.js";
import {
  duplicateCandidatesOf,
  fieldErrorsOf,
  orderMutationErrorMessage,
  useCreateCase,
  type CaseMutationResultDto,
  type DuplicateCandidate,
} from "../../../api/caseMutations.js";
import { Button } from "../../../ui/Button.js";
import { DEFAULT_PARTS_VIEW } from "../presentation.js";
import { PartsShell } from "../workspace/PartsShell.js";
import { viewPath } from "../workspace/PartsSidebar.js";
import { parseWorkspaceUrlState } from "../workspace/useWorkspaceUrlState.js";
import { DuplicateWarningDialog } from "./DuplicateWarningDialog.js";
import {
  clearChangedLineMarks,
  emptyOrderFormValues,
  focusFirstInvalid,
  isPristineOrderForm,
  MasterEtaField,
  newLineValues,
  NO_ERRORS,
  OptionalDetailsFields,
  OrderFormSection,
  orderFormErrorsFromServer,
  PeopleFields,
  toCreateCaseInput,
  validateOrderForm,
  VehicleAndVisitFields,
  withMasterEta,
  withoutBlankTrailingLines,
  type OrderFormErrors,
  type OrderFormHeaderValues,
  type OrderFormValues,
} from "./OrderForm.js";
import { PartLinesGrid } from "./PartLinesGrid.js";
import { UnsavedChangesGuard } from "./UnsavedChangesGuard.js";

/**
 * `/parts/new` — manual order creation (spec §14).
 *
 * The four sections — Vehicle and visit, People, Parts ordered, Optional
 * details — end in Cancel, Create order, and Create and print. Validation
 * runs in the browser on submit and marks each field inline (the server
 * validates again and its field errors land in the same places). One
 * successful `POST /api/cases` creates exactly one case: while it is in
 * flight both finishing buttons are disabled, and only its response decides
 * where the page goes — the created case in the work queue, or the receipt
 * with `?autoprint=1` for Create and print. A 409 `DUPLICATE_CASE` opens the
 * duplicate warning; proceeding resubmits the unchanged input with the
 * override reason.
 *
 * Leaving with something typed asks first (in-app navigation and the
 * browser's own unload prompt); Cancel is an explicit choice and discards
 * without asking. The page sits in the `PartsShell`, which is Parts' own —
 * any other role is sent home.
 */

type Intent = "create" | "create_and_print";

/** Where a created case is shown for each way of finishing. */
export function createdCasePath(caseId: string, intent: Intent): string {
  const id = encodeURIComponent(caseId);
  return intent === "create_and_print"
    ? `/cases/${id}/print?autoprint=1`
    : `/parts?view=${DEFAULT_PARTS_VIEW}&case=${id}`;
}

export function NewOrderPage() {
  return (
    <PartsShell page activeDestination="new_order">
      <NewOrderForm />
    </PartsShell>
  );
}

type DuplicateState = Readonly<{
  candidates: readonly DuplicateCandidate[];
  intent: Intent;
}>;

function NewOrderForm() {
  const navigate = useNavigate();
  const titleId = useId();
  const idPrefix = useId();
  const formRef = useRef<HTMLFormElement>(null);

  const [values, setValues] = useState<OrderFormValues>(emptyOrderFormValues);
  const [errors, setErrors] = useState<OrderFormErrors>(NO_ERRORS);
  const [formError, setFormError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateState | null>(null);
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  const [pendingIntent, setPendingIntent] = useState<Intent | null>(null);

  const assignees = useAssignees();
  const create = useCreateCase();

  // Read at navigation time by the guard: what is typed now, and whether the
  // order has since been created (or Cancel chosen), in which case there is
  // nothing left to lose.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const leavingRef = useRef(false);
  const hasUnsavedWork = useCallback(
    () => !leavingRef.current && !isPristineOrderForm(valuesRef.current),
    [],
  );

  useEffect(() => {
    if (focusRequest > 0) focusFirstInvalid(formRef.current);
  }, [focusRequest]);

  const updateHeader = (patch: Partial<OrderFormHeaderValues>) => {
    setValues((current) => ({
      ...current,
      ...patch,
      // "ETA for all parts" fills the blank lines as it is typed (spec §6.5).
      ...(patch.masterEta !== undefined
        ? { partLines: withMasterEta(current.partLines, patch.masterEta) }
        : {}),
    }));
    // A corrected field stops being marked at once.
    setErrors((current) => {
      const fields = { ...current.fields };
      let changed = false;
      for (const key of Object.keys(patch) as (keyof OrderFormHeaderValues)[]) {
        if (fields[key]) {
          delete fields[key];
          changed = true;
        }
      }
      return changed ? { ...current, fields } : current;
    });
  };

  const updateLines = (partLines: OrderFormValues["partLines"]) => {
    const previousLines = values.partLines;
    // A line that goes blank again (No ETA unticked, a date cleared) takes
    // the master ETA back at once: under one, a blank line means that date.
    setValues((current) => ({ ...current, partLines: withMasterEta(partLines, current.masterEta) }));
    // Clear the marks on line fields whose text changed.
    setErrors((current) => {
      const lines = clearChangedLineMarks(current.lines, previousLines, partLines);
      return lines === current.lines ? current : { ...current, lines };
    });
  };

  const finish = (result: CaseMutationResultDto, intent: Intent) => {
    leavingRef.current = true;
    navigate(createdCasePath(result.caseId, intent));
  };

  // What a submit sends: the form less the untouched rows at the end (the
  // row Enter adds after the last part), so that row is never refused.
  const submitted = (): OrderFormValues => ({ ...values, partLines: withoutBlankTrailingLines(values.partLines) });

  const submit = (intent: Intent) => {
    // One successful creation is one order: a click that lands after the
    // response and before the page has moved on must not create another.
    if (create.isPending || leavingRef.current) return;
    const input = submitted();
    const validation = validateOrderForm(input, new Date());
    setErrors(validation);
    setFormError(null);
    if (!validation.isValid) {
      setFocusRequest((n) => n + 1);
      return;
    }
    setPendingIntent(intent);
    create.mutate(toCreateCaseInput(input), {
      onSuccess: (result) => finish(result, intent),
      onError: (error) => {
        const candidates = duplicateCandidatesOf(error);
        if (candidates) {
          setDuplicate({ candidates, intent });
          setOverrideError(null);
          return;
        }
        const fieldErrors = fieldErrorsOf(error);
        if (fieldErrors) {
          const mapped = orderFormErrorsFromServer(fieldErrors, input);
          setErrors(mapped.errors);
          setFormError(mapped.unmapped.length > 0 ? mapped.unmapped.join(" ") : null);
          if (!mapped.errors.isValid) setFocusRequest((n) => n + 1);
          else if (mapped.unmapped.length === 0) setFormError(orderMutationErrorMessage(error));
          return;
        }
        setFormError(orderMutationErrorMessage(error));
      },
      onSettled: () => setPendingIntent(null),
    });
  };

  const proceedWithOverride = (reason: string) => {
    if (!duplicate || create.isPending || leavingRef.current) return;
    const { intent } = duplicate;
    const input = submitted();
    setOverrideError(null);
    create.mutate(toCreateCaseInput(input, { duplicateOverrideReason: reason }), {
      onSuccess: (result) => finish(result, intent),
      onError: (error) => {
        const fieldErrors = fieldErrorsOf(error);
        const reasonMessage = fieldErrors?.["duplicateOverrideReason"]?.find((text) => text.length > 0);
        if (reasonMessage) {
          setOverrideError(reasonMessage);
          return;
        }
        if (duplicateCandidatesOf(error)) {
          setOverrideError("The server still sees a duplicate. Check the reason and try again.");
          return;
        }
        // Anything else belongs on the form: close the warning and show it there.
        setDuplicate(null);
        if (fieldErrors) {
          const mapped = orderFormErrorsFromServer(fieldErrors, input);
          setErrors(mapped.errors);
          setFormError(mapped.unmapped.length > 0 ? mapped.unmapped.join(" ") : null);
          if (!mapped.errors.isValid) setFocusRequest((n) => n + 1);
          return;
        }
        setFormError(orderMutationErrorMessage(error));
      },
    });
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submitter = (event.nativeEvent as SubmitEvent).submitter;
    const intent: Intent =
      submitter instanceof HTMLButtonElement && submitter.value === "create_and_print"
        ? "create_and_print"
        : "create";
    submit(intent);
  };

  const cancel = () => {
    leavingRef.current = true;
    navigate(viewPath(parseWorkspaceUrlState(new URLSearchParams()), DEFAULT_PARTS_VIEW));
  };

  const busy = create.isPending;

  return (
    <div className="order-page">
      <header className="order-page__header">
        <h1 id={titleId} className="order-page__title">
          New order
        </h1>
        <p className="order-page__lead">
          Enter the vehicle, the people, and every part ordered. Nothing is saved until you create the order.
        </p>
      </header>

      <form
        ref={formRef}
        className="order-form"
        aria-labelledby={titleId}
        noValidate
        onSubmit={onSubmit}
      >
        <OrderFormSection title="Vehicle and visit">
          <VehicleAndVisitFields
            values={values}
            errors={errors.fields}
            onChange={updateHeader}
            disabled={busy}
            idPrefix={idPrefix}
          />
        </OrderFormSection>

        <OrderFormSection title="People">
          <PeopleFields
            values={values}
            errors={errors.fields}
            onChange={updateHeader}
            disabled={busy}
            idPrefix={idPrefix}
            assignees={assignees.data}
            assigneesLoading={assignees.isPending}
            assigneesError={assignees.error}
            onRetryAssignees={() => {
              void assignees.refetch();
            }}
          />
        </OrderFormSection>

        <OrderFormSection
          title="Parts ordered"
          description="Add every part for this order. Each line needs an ETA date or an explicit No ETA — or one ETA for all parts."
        >
          <MasterEtaField
            value={values.masterEta}
            error={errors.fields.masterEta}
            onChange={(masterEta) => updateHeader({ masterEta })}
            disabled={busy}
            idPrefix={idPrefix}
          />
          <PartLinesGrid
            lines={values.partLines}
            errors={errors.lines}
            onChange={updateLines}
            createLine={() => newLineValues(values.masterEta)}
            disabled={busy}
            idPrefix={idPrefix}
          />
        </OrderFormSection>

        <OrderFormSection title="Optional details">
          <OptionalDetailsFields
            values={values}
            errors={errors.fields}
            onChange={updateHeader}
            disabled={busy}
            idPrefix={idPrefix}
          />
        </OrderFormSection>

        {formError ? (
          <p role="alert" className="form-alert order-form__alert">
            {formError}
          </p>
        ) : null}

        <div className="order-form__actions">
          <Button variant="secondary" onClick={cancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            name="intent"
            value="create"
            variant="primary"
            busy={busy && pendingIntent === "create"}
            disabled={busy}
          >
            Create order
          </Button>
          <Button
            type="submit"
            name="intent"
            value="create_and_print"
            variant="primary"
            icon={<Printer />}
            busy={busy && pendingIntent === "create_and_print"}
            disabled={busy}
          >
            Create and print
          </Button>
        </div>
      </form>

      <UnsavedChangesGuard when={hasUnsavedWork} />

      <DuplicateWarningDialog
        open={duplicate !== null}
        candidates={duplicate?.candidates ?? []}
        onClose={() => setDuplicate(null)}
        onProceed={proceedWithOverride}
        pending={busy}
        error={overrideError}
        proceedLabel="Create anyway"
      />
    </div>
  );
}
